import { PageLayout } from "@/components/layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ErrorState } from "@/components/ui/error-state";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useDeleteInstitutionStaff,
  useInstitutionStaff,
} from "@/hooks/useInstitutionStaff";
import { usePermissions } from "@niutraan/hooks";
import type { InstitutionStaffDto } from "@niutraan/shared-dto";
import { PermissionEnum } from "@niutraan/shared-dto";
import { Book, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function InstitutionStaffScreen() {
  const { hasPermission } = usePermissions();

  // Check specific permissions

  const canCreateStaff = hasPermission(PermissionEnum.INSTITUTION_STAFF_WRITE);
  const canUpdateStaff = hasPermission(PermissionEnum.INSTITUTION_STAFF_UPDATE);
  const canDeleteStaff = hasPermission(PermissionEnum.INSTITUTION_STAFF_DELETE);

  const [searchQuery, setSearchQuery] = useState("");
  const [staffToDelete, setStaffToDelete] =
    useState<InstitutionStaffDto | null>(null);

  const deleteStaffMutation = useDeleteInstitutionStaff();

  // Use React Query hook to fetch data
  const {
    data: institutionStaff,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useInstitutionStaff();

  // Filter staff based on search query
  const filteredStaff =
    institutionStaff?.filter((staff) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        staff.user?.firstName?.toLowerCase().includes(searchLower) ||
        false ||
        staff.user?.lastName?.toLowerCase().includes(searchLower) ||
        false ||
        staff.role?.name?.toLowerCase().includes(searchLower) ||
        false ||
        staff.institutionLocation?.name?.toLowerCase().includes(searchLower) ||
        false
      );
    }) || [];

  // Handle delete staff
  const handleDeleteStaff = (staff: InstitutionStaffDto) => {
    setStaffToDelete(staff);
  };

  // Confirm delete staff
  const confirmDeleteStaff = async () => {
    if (!staffToDelete) return;

    try {
      await deleteStaffMutation.mutateAsync(staffToDelete.id);
      toast.success(
        `${staffToDelete.user?.firstName} has been deleted successfully.`,
      );
      refetch();
    } catch (error) {
      toast.error(
        `Failed to delete ${staffToDelete.user?.firstName}. Please try again.`,
      );
      console.error("Error deleting institution:", error);
    } finally {
      // Close the dialog
      setStaffToDelete(null);
    }
  };

  // Show unauthorized state if user doesn't have view permission
  if (!canCreateStaff) {
    return (
      <PageLayout title="Unauthorized">
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
          <div className="p-4 rounded-full bg-muted mb-4">
            <Book className="h-12 w-12 text-muted-foreground" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground max-w-md mb-6">
            You do not have permission to view locations.
          </p>
          <Button asChild>
            <Link to="/">Go Back</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Show loading state
  if (isLoading && !isRefetching) {
    return (
      <PageLayout
        title="Manage Institutions Staff"
        description="Loading Institutions Staff..."
        breadcrumbs={[
          { label: "Admin", href: "/admin" },
          { label: "Institutions Staff" },
        ]}
      >
        <div className="space-y-4">
          <Skeleton className="h-[120px] w-full rounded-lg" />
          <Skeleton className="h-[120px] w-full rounded-lg" />
          <Skeleton className="h-[120px] w-full rounded-lg" />
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <PageLayout
        title="Error"
        breadcrumbs={[
          { label: "Admin", href: "/admin" },
          { label: "Institution Staff" },
        ]}
      >
        <ErrorState
          title="Error loading Institution Staffs"
          error={error}
          icon={<Book className="h-12 w-12 text-destructive" />}
          onAction={() => refetch()}
          actionText="Try Again"
          containerSize="w-full"
        />
      </PageLayout>
    );
  }

  // Create action buttons for the page header
  const actionButtons = (
    <div className="flex gap-2">
      <Button onClick={() => refetch()} variant="outline">
        Refresh
      </Button>
      {canCreateStaff && (
        <Button asChild>
          <Link to="/admin/institutions/staff/new">
            <Plus className="mr-2 h-4 w-4" /> Add Institution Staff
          </Link>
        </Button>
      )}
    </div>
  );

  return (
    <PageLayout
      title="Manage Institution Staff"
      description={`${filteredStaff.length} Institution staff${filteredStaff.length !== 1 ? "s" : ""}  available`}
      actions={actionButtons}
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Institution Staff" },
      ]}
    >
      <div className="mb-6">
        <Input
          placeholder="Search Staff..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>

      {filteredStaff.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
          <Book className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">
            No Institution Staff Found
          </h2>
          <p className="text-muted-foreground max-w-md mb-6">
            {searchQuery
              ? "No Institutions match your search criteria. Try a different search term."
              : "You haven't created any Institutions yet. Click the button below to add your first Institutions."}
          </p>
          {canCreateStaff && !searchQuery && (
            <Button asChild>
              <Link to="/admin/institutions/staff/new">
                <Plus className="mr-2 h-4 w-4" /> Add Institution Staff
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredStaff.map((staff: InstitutionStaffDto) => {
            return (
              <Card key={staff.id} className="overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-left">
                      {" "}
                      {staff.user?.firstName} {staff.user?.lastName}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="space-y-0">
                    <div className="flex items-center justify-between text-sm">
                      <p> {staff.role?.name || "No Role Assigned"}</p>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <p>
                        {" "}
                        {staff.institutionLocation?.name ||
                          "No Location Assigned"}
                      </p>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      {!staff.isActive && (
                        <p className="text-red-500 ">Inactive</p>
                      )}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between pt-3">
                  {canUpdateStaff && (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/admin/institutions/staff/${staff.id}`}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit
                      </Link>
                    </Button>
                  )}
                  {canDeleteStaff && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteStaff(staff)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog
        open={!!staffToDelete}
        onOpenChange={(open) => !open && setStaffToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete institution staff</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{staffToDelete?.user?.firstName}
              "? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteStaff}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
